import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Minus, Sparkles, Activity, Brain, Heart, AlertCircle } from 'lucide-react';
import { useUnifiedWellnessScore } from '@/hooks/useUnifiedWellnessScore';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

export function UnifiedWellnessScore() {
  const { score, isLoading, hasWhoopData } = useUnifiedWellnessScore();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Card className="border-2 animate-pulse">
        <CardHeader>
          <div className="h-8 bg-muted rounded w-3/4 mb-2" />
          <div className="h-4 bg-muted rounded w-1/2" />
        </CardHeader>
        <CardContent>
          <div className="h-32 bg-muted rounded" />
        </CardContent>
      </Card>
    );
  }

  if (!score) {
    return (
      <Card className="border-2 border-dashed">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            Unified Wellness Score
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">
            Complete your daily check-ins to unlock your Unified Wellness Score - a single metric that combines your physical health, mental wellbeing, and recovery.
          </p>
          <Button onClick={() => navigate('/daily-check-in')} className="w-full">
            Start Check-In
          </Button>
        </CardContent>
      </Card>
    );
  }

  const statusConfig = {
    optimal: {
      color: 'from-accent/20 to-primary/20',
      border: 'border-accent/50',
      badge: 'bg-accent/10 text-accent border-accent/30',
      icon: Sparkles,
      iconColor: 'text-accent'
    },
    good: {
      color: 'from-primary/20 to-accent/20',
      border: 'border-primary/50',
      badge: 'bg-primary/10 text-primary border-primary/30',
      icon: TrendingUp,
      iconColor: 'text-primary'
    },
    moderate: {
      color: 'from-secondary/20 to-muted/20',
      border: 'border-secondary/50',
      badge: 'bg-secondary/10 text-secondary border-secondary/30',
      icon: Minus,
      iconColor: 'text-secondary'
    },
    'attention-needed': {
      color: 'from-destructive/20 to-destructive/30',
      border: 'border-destructive/50',
      badge: 'bg-destructive/10 text-destructive border-destructive/30',
      icon: AlertCircle,
      iconColor: 'text-destructive'
    },
  };

  const config = statusConfig[score.status];
  const TrendIcon = score.trend === 'improving' ? TrendingUp : score.trend === 'declining' ? TrendingDown : Minus;
  const trendColor = score.trend === 'improving' ? 'text-accent' : score.trend === 'declining' ? 'text-destructive' : 'text-muted-foreground';

  return (
    <Card className={`border-2 ${config.border} bg-gradient-to-br ${config.color}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <config.icon className={`w-6 h-6 ${config.iconColor}`} />
            Unified Wellness Score
          </CardTitle>
          <Badge variant="outline" className={config.badge}>
            {score.status.replace('-', ' ').toUpperCase()}
          </Badge>
        </div>
        <p className="text-base text-muted-foreground">
          Your complete mind-body health in one metric
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Main Score */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-6xl font-bold">{score.overall}</div>
            <p className="text-base text-muted-foreground">out of 100</p>
          </div>
          <div className={`flex items-center gap-2 ${trendColor}`}>
            <TrendIcon className="w-8 h-8" />
            <div className="text-right">
              <div className="text-base font-medium">7-day trend</div>
              <div className="text-sm">{score.trend}</div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <Progress value={score.overall} className="h-3" />
        </div>

        {/* Message */}
        <p className="text-base font-medium">{score.message}</p>

        {/* Breakdown */}
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-base">
              <Activity className="w-5 h-5 text-accent" />
              <span>Physical</span>
            </div>
            <div className="text-2xl font-bold">{score.breakdown.physical}</div>
            <Progress value={score.breakdown.physical} className="h-2" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-base">
              <Brain className="w-5 h-5 text-primary" />
              <span>Mental</span>
            </div>
            <div className="text-2xl font-bold">{score.breakdown.mental}</div>
            <Progress value={score.breakdown.mental} className="h-2" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-base">
              <Heart className="w-5 h-5 text-secondary" />
              <span>Recovery</span>
            </div>
            <div className="text-2xl font-bold">{score.breakdown.recovery}</div>
            <Progress value={score.breakdown.recovery} className="h-2" />
            {!hasWhoopData && (
              <p className="text-sm text-muted-foreground">*Estimated</p>
            )}
          </div>
        </div>

        {/* Recommendations */}
        {score.recommendations.length > 0 && (
          <div className="space-y-2">
            <div className="text-base font-semibold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Personalised Actions
            </div>
            <ul className="space-y-2">
              {score.recommendations.map((rec, idx) => (
                <li key={idx} className="text-base pl-4 border-l-2 border-primary/30">
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Quick Actions */}
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate('/my-scores')}
            className="flex-1"
          >
            View Details
          </Button>
          {score.status === 'attention-needed' ? (
            <Button 
              size="sm" 
              onClick={() => navigate('/therapy')}
              className="flex-1"
            >
              Get Support
            </Button>
          ) : (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/vault?tab=correlations')}
              className="flex-1"
            >
              Explore Patterns
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
